// src/pages/DeviceDetailPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  View,
  Flex,
  Heading,
  Text,
  Card,
  Badge,
  Button,
  Alert,
  Divider,
  Loader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@aws-amplify/ui-react";
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import { gql } from "../lib/gqlClient";
import HealthBadge from "../components/common/HealthBadge"; 
import { 
  Info, 
  AlertTriangle,
  CheckCircle2,
  History,
  BatteryFull,
  BatteryMedium,
  BatteryWarning,
  WifiOff,
  Rotate3D
} from "lucide-react";
import DataFieldWithHelp from "../components/common/DataFieldWithHelp";
import { normalizeGraphQLError } from "../utils/graphqlErrors";
import { useAdminTenant } from "../admin/AdminTenantContext";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export function formatUptime(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));

  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatShortDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function DeviceDetailPage() {
  const { deviceId } = useParams(); // route should define :deviceId
  const navigate = useNavigate();

  const [device, setDevice] = useState(null);
  const [deviceError, setDeviceError] = useState("");
  const [actionError, setActionError] = useState("");
  const [loadingDevice, setLoadingDevice] = useState(true);

  const [latestTelemetry, setLatestTelemetry] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [loadingTelemetry, setLoadingTelemetry] = useState(true);
  const [telemetryError, setTelemetryError] = useState("");

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const [isActivateToggling, setIsActivateToggling] = useState(false);

  const location = useLocation();

  const { tenantKey, isPlatformAdmin } = useAdminTenant();

  const flash = location.state?.flash;

  // Fetch core data
  useEffect(() => {
    if (!deviceId) return;

    let cancelled = false;

    async function fetchAll() {
      // Device
      try {
        setLoadingDevice(true);
        const data = await gql(queries.getDevice, { id: deviceId });
        if (cancelled) return;

        const d = data?.getDevice ?? null;
        if (!d) {
          setDeviceError("Device not found.");
          setDevice(null);
        } else {
          setDeviceError(null);
          setDevice(d);
        }
      } catch (e) {
        console.error("getDevice error", e);
        if (!cancelled) {
          // gql throws Error(res.errors[0].message), so e.message is what you want
          setDeviceError(e?.message || "Failed to load device.");
        }
      } finally {
        if (!cancelled) setLoadingDevice(false);
      }

      // Telemetry: latest + last 24h
      try {
        setLoadingTelemetry(true);

        const [latestResData, listResData] = await Promise.all([
          gql(queries.latestTelemetry, { device_id: deviceId }),
          (async () => {
            const now = new Date();
            const since = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
            return gql(queries.listTelemetry, {
              device_id: deviceId,
              since,
              limit: 200,
            });
          })(),
        ]);

        if (cancelled) return;
        setLatestTelemetry(latestResData?.latestTelemetry ?? null);
        setTelemetry(listResData?.listTelemetry ?? []);
      } catch (e) {
        console.error("telemetry error", e);
        if (!cancelled) {
          setTelemetryError(e?.message || "Failed to load telemetry.");
        }
      } finally {
        if (!cancelled) setLoadingTelemetry(false);
      }

      // Events
      (async () => {
        try {
          setLoadingEvents(true);
          setEventsError("");
          const items = await fetchEventsData(deviceId);
          if (!cancelled) setEvents(items);
        } catch (e) {
          console.error("eventsByDeviceTime error", e);
          if (!cancelled) setEventsError(e?.message || "Failed to load events.");
        } finally {
          if (!cancelled) setLoadingEvents(false);
        }
      })();
    }

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  async function fetchEventsData(deviceId) {
    const evResData = await gql(queries.eventsByDeviceTime, {
      deviceId,
      sortDirection: "DESC",
      limit: 20,
    });
    return evResData?.eventsByDeviceTime?.items ?? [];
  }

  const lastSeenLabel = useMemo(() => {
    if (!device?.lastTelemetryAt) return "Never";
    return formatDateTime(device.lastTelemetryAt);
  }, [device?.lastTelemetryAt]);

  const lastTelemSnapshot = latestTelemetry || (telemetry.length > 0 ? telemetry[0] : null);

  const leakActiveFromTelemetry = !!(
    lastTelemSnapshot &&
    (lastTelemSnapshot.leak1 || lastTelemSnapshot.leak2)
  );

  const leakStatusText = useMemo(() => {
    if (!device) return { status: "none" };

    if (!lastTelemSnapshot) {
      return { status: "unknown" };
    }

    if (device.activeLeak) {
      return {
        status: "active",
        since: device.leakStartedAt ? formatDateTime(device.leakStartedAt) : null,
      };
    }

    if (device.leakStartedAt && device.leakClearedAt) {
      return {
        status: "past",
        text: `Last leak: ${formatShortDateTime(device.leakStartedAt)} – ${formatShortDateTime(
          device.leakClearedAt
        )}`,
      };
    }

    return { status: "none" };
  }, [device, lastTelemSnapshot]);

  function leakClearedRecently(leakClearedAt, days = 60) {
    if (!leakClearedAt) return false;
    const cleared = new Date(leakClearedAt).getTime();
    if (Number.isNaN(cleared)) return false;

    const now = Date.now();
    return (now - cleared) <= days * 24 * 60 * 60 * 1000;
  }

  const pageTitle = device?.label || device?.id || "Device details";

  function getBatteryStatus(v, lowBatteryActive) {
    if (lowBatteryActive === true) {
        return { label: "Replace", variation: "error" };
    }
    if (typeof v !== "number") {
        return { label: "Unknown", variation: "neutral" };
    }
    if (v >= 3.05) {
        return { label: "Fresh", variation: "success" };
    }
    if (v < 2.90) {
        return { label: "Replace", variation: "error" };
    }
    return { label: "Fair", variation: "neutral" };
  }

  const batteryMeta = getBatteryStatus(device?.lastBattV, device?.lowBatteryActive);

  const handleToggleActive = async () => {
      if (!device?.id || isActivateToggling) return;

      const nextIsActive = !device.isActive;

      const ok = window.confirm(
        device.isActive
          ? "Deactivate this device?\n\nIt will be hidden from active lists."
          : "Reactivate this device?\n\nIt will appear in active lists again."
      );
      if (!ok) return;

      try {
        setIsActivateToggling(true);

        const input = {id: deviceId};

        const data = await gql(
          mutations.toggleDeviceActive, 
          {tenantKey, input}
        );
        setDevice((prev) =>
          data?.toggleDeviceActive ?? {
            ...prev,
            isActive: nextIsActive,
          }
        );

        setLoadingEvents(true);
        const items = await fetchEventsData(deviceId);
        setEvents(items);

        navigate(location.pathname, {
          replace: true,
          state: { flash: nextIsActive ? "Device reactivated." : "Device deactivated." },
        });
      } catch (err) {
        console.error(err);
        setActionError(normalizeGraphQLError(err) || "Failed to update device status.");
      } finally {
        setIsActivateToggling(false);
        setLoadingEvents(false);
      }
  };

  const activateTooltipText = device?.isActive
  ? "Deactivate hides this device from active lists. It continues reporting data and generating events."
  : "Reactivate shows this device in active lists again.";

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="View Device Detail"
        message="Select a tenant in the top bar before viewing device detail."
      />
    );
  }

  return (
    <View maxWidth="72rem" margin="0 auto" padding="1.5rem">
      {/* Back link */}
      <Button
        variation="link"
        size="small"
        onClick={() => navigate("/devices")}
        marginBottom="0.5rem"
      >
        ← All devices
      </Button>

      {/* Page heading + status badges */}
      <Flex justifyContent="space-between" alignItems="flex-start" gap="1rem" marginBottom="1rem">
        <View>
          <Heading level={3}>{pageTitle}</Heading>
          {device?.label && (
            <Text variation="tertiary" fontSize="0.85rem">
              Device ID: {device.id}
            </Text>
          )}

          <Text variation="tertiary" fontSize="0.75rem" marginTop="0.35rem">
            Last seen: {lastSeenLabel}
          </Text>
          {device?.firstOnlineAt && (
            <Text variation="tertiary" fontSize="0.75rem">
              First online: {formatDateTime(device.firstOnlineAt)}
            </Text>
          )}
        </View>

        <Flex direction="column" gap="0.35rem" alignItems="flex-end">
          <Flex gap="0.35rem" wrap="wrap" justifyContent="flex-end">
            {/* Health badge */}
             <HealthBadge status={device?.healthStatus} badgeSize={device?.healthStatus === "LEAK" ? "large" : "small"} />

            {/* Active / inactive */}
            {!device?.isActive && (
              <Badge 
              variation="neutral" 
              size="small"
              title="This device is archived and hidden from active lists. It still reports data.">
                Inactive
              </Badge>
            )}
          </Flex>

          {loadingDevice && <Loader size="small" />}
        </Flex>
      </Flex>
      <View marginTop="1rem" marginBottom="1rem">
        {device && !device.isActive && (
          <Alert variation="info" hasIcon marginBottom="0.5rem">
            This device is inactive and hidden from active lists. It will continue reporting data and generating events.
          </Alert>
        )}
        {flash && (
          <Alert
            variation="success"
            hasIcon
            isDismissible
            onDismiss={() =>
              navigate(location.pathname, {
                replace: true,
                state: {},
              })
            }
          >
            {flash}
          </Alert>
        )}

        {actionError.global && (
          <Alert
            variation="error"
            hasIcon
            isDismissible
            onDismiss={() => setActionError("")}
            marginTop="0.5rem"
            heading="Action failed"
          >
            {actionError.global}
          </Alert>
        )}

        {deviceError && (
          <Alert
            variation="error"
            isDismissible={true}
            onDismiss={() => setDeviceError("")}
            heading="Unable to load device"
          >
            {deviceError}
          </Alert>
        )}
      </View>

      {/* Main layout: two columns on desktop, stacked on mobile */}
      <Flex direction={{ base: "column", large: "row" }} gap="1rem">
        {/* Left column */}
        <Flex direction="column" gap="1rem" width={{ base: "100%", large: "38%" }}>
          {/* Overview & location */}
          <Card variation="outlined">
            <Heading level={5} marginBottom="0.5rem">
              Overview
            </Heading>

            {loadingDevice && !device && <Loader />}

            {device && (
              <Flex direction="column" gap="0.35rem">
                {/* Identity */}
                <Text fontWeight={600}>{device.label || device.id}</Text>
                {device.label && (
                  <Text variation="tertiary" fontSize="0.8rem">
                    ID: {device.id}
                  </Text>
                )}

                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                {/* Location */}
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Property:
                  </Text>{" "}
                  {device.propertyCode || device.propertyId || "—"}
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Building:
                  </Text>{" "}
                  {device.buildingCode || device.buildingId || "—"}
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Unit:
                  </Text>{" "}
                  {device.unitCode || device.unitId || "—"}
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Room:
                  </Text>{" "}
                  {device.room || "—"}
                </Text>

                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                {/* Meta */}
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Appliance:
                  </Text>{" "}
                  {device.assetType || "—"}
                </Text>
                <Text fontSize="0.85rem" marginTop="0.35rem">
                  <Text as="span" fontWeight={600}>
                    Claimed:
                  </Text>{" "}
                  {device.claimedAt ? formatDateTime(device.claimedAt) : "—"}
                </Text>

                {/* Notes */}
                {device.notes && (
                  <>
                    <Divider marginTop="0.5rem" marginBottom="0.5rem" />
                    <Text fontWeight={600} fontSize="0.9rem">
                      Notes
                    </Text>
                    <Text fontSize="0.85rem" whiteSpace="pre-wrap">
                      {device.notes}
                    </Text>
                  </>
                )}

                {/* Actions */}
                <Flex marginTop="0.75rem" gap="0.5rem">
                  <Button
                    size="small"
                    variation="primary"
                    isDisabled={!device?.id}
                    onClick={() => {
                      navigate(`/devices/${device.id}/edit`);
                    }}
                  >
                    Edit details
                  </Button>
                  <Button
                    title={activateTooltipText}
                    size="small"
                    variation={device.isActive ? "secondary" : "primary"}
                    isLoading={isActivateToggling}
                    onClick={handleToggleActive}
                  >
                    {device.isActive ? "Deactivate" : "Reactivate"}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Card>

          {/* Health & environment */}
          <Card variation="outlined">
            <Heading level={5} marginBottom="0.5rem">
              Health & Environment
            </Heading>

            {loadingDevice && !device && <Loader />}

            {device && (
              <Flex direction="column" gap="0.35rem">
                <Text fontWeight={600} fontSize="0.9rem">
                  Health
                </Text>
                {/* Connectivity / offline row */}
                {device?.healthStatus === "OFFLINE" && (
                  <Flex alignItems="center" gap="0.35rem">
                    <WifiOff
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-red-60)"
                    />
                    <Text fontSize="0.85rem">
                      <Text as="span" fontWeight={600}>
                        Connectivity:
                      </Text>{" "}
                      Device is currently offline. Last check-in{" "}
                      {device.lastTelemetryAt ? formatShortDateTime(device.lastTelemetryAt) : "unknown"}.
                    </Text>
                  </Flex>
                )}

                {device?.tiltActive === true && (
                  <Alert variation="warning" hasIcon={false}>
                    <Flex alignItems="flex-start" gap="0.5rem">
                      <Rotate3D
                        size={18}
                        strokeWidth={2}
                        color="var(--amplify-colors-orange-80)"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Text fontSize="0.85rem">
                        <Text as="span" fontWeight={700}>
                          Check placement:
                        </Text>{" "}
                        Device may be tilted. Check that the device is flat and correctly positioned.
                      </Text>
                    </Flex>
                  </Alert>
                )}

                {/* Leak status row */}
                <Flex alignItems="center" gap="0.35rem">
                  {device.activeLeak ? (
                    <AlertTriangle
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-red-60)"
                    />
                  ) : leakClearedRecently(device.leakClearedAt, 60) ? (
                    <History
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-orange-60)"
                    />
                  ) : !lastTelemSnapshot ? (
                    null
                  ) : (
                    <CheckCircle2
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-green-60)"
                    />
                  )}
                  <Flex alignItems="center" gap="0.4rem">
                    <Text 
                      fontSize="0.85rem" 
                      as="span"   
                      fontWeight={600}
                      whiteSpace="nowrap"
                    >
                      Leak status:
                    </Text>

                    {/* UNKNOWN / NO TELEMETRY YET */}
                    {leakStatusText.status === "unknown" && (
                      <Text fontSize="0.85rem">—</Text>
                    )}

                    {/* ACTIVE LEAK */}
                    {leakStatusText.status === "active" && (
                      <Flex alignItems="center" gap="0.4rem">
                        <Badge variation="error" size="small">
                          Active leak
                        </Badge>

                        {leakStatusText.since && (
                          <Text fontSize="0.85rem" as="span">
                            since {leakStatusText.since}
                          </Text>
                        )}
                      </Flex>
                    )}

                    {/* PAST LEAK */}
                    {leakStatusText.status === "past" && (
                      <Text fontSize="0.85rem">{leakStatusText.text}</Text>
                    )}

                    {/* NO LEAK */}
                    {leakStatusText.status === "none" && (
                      <Text fontSize="0.85rem">No active leak</Text>
                    )}
                  </Flex>
                </Flex>
                {/* Battery status row */}
                <Flex alignItems="center" gap="0.35rem">
                  {batteryMeta.label === "Replace" ? (
                    <BatteryWarning
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-red-60)"
                    />
                  ) : batteryMeta.label === "Fresh" ? (
                    <BatteryFull
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-green-60)"
                    />
                  ) : (
                    <BatteryMedium
                      size={18}
                      strokeWidth={2}
                      color="var(--amplify-colors-neutral-80)"
                    />
                  )}

                  <Text fontSize="0.85rem">
                    <Text as="span" fontWeight={600}>
                      Battery:
                    </Text>{" "}
                    {typeof device?.lastBattV === "number"
                      ? `${device.lastBattV.toFixed(2)} V`
                      : "—"}
                  </Text>

                  {batteryMeta.label === "Replace" && (
                    <Badge size="small" variation={batteryMeta.variation}>
                      {batteryMeta.label}
                    </Badge>
                  )}
                </Flex>

                {batteryMeta.label !== "Replace" && (
                  <Text
                    variation="tertiary"
                    fontSize="0.75rem"
                    marginLeft="1.8rem"
                  >
                    Replace batteries at ~2.90 V.
                  </Text>
                )}

                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                <Text fontWeight={600} fontSize="0.9rem">
                  Latest environment
                </Text>
                {loadingTelemetry && !lastTelemSnapshot && <Loader size="small" />}
                {lastTelemSnapshot ? (
                  <>
                    <Text fontSize="0.85rem">
                      <Text as="span" fontWeight={600}>
                        Last reading:
                      </Text>{" "}
                      {formatDateTime(lastTelemSnapshot.received_at)}
                    </Text>
                    <Text fontSize="0.85rem">
                      <Text as="span" fontWeight={600}>
                        Temperature:
                      </Text>{" "}
                      {typeof lastTelemSnapshot.tempC === "number"
                        ? `${lastTelemSnapshot.tempC.toFixed(1)} °C`
                        : "—"}
                    </Text>
                    <Text fontSize="0.85rem">
                      <Text as="span" fontWeight={600}>
                        Humidity:
                      </Text>{" "}
                      {typeof lastTelemSnapshot.rh === "number"
                        ? `${lastTelemSnapshot.rh.toFixed(1)} %`
                        : "—"}
                    </Text>
                    <Text fontSize="0.85rem">
                      <Text as="span" fontWeight={600}>
                        Leak sensors:
                      </Text>{" "}
                      {leakActiveFromTelemetry ? "Wet" : "Dry"}
                    </Text>

                  </>
                ) : (
                  !loadingTelemetry && (
                    <Text fontSize="0.85rem" variation="tertiary">
                      No telemetry received yet.
                    </Text>
                  )
                )}
              </Flex>
            )}
          </Card>

          {/* Connectivity & firmware */}
          <Card variation="outlined">
            <Heading level={5} marginBottom="0.5rem">
              Connectivity & Firmware
            </Heading>

            {device ? (
              <Flex direction="column" gap="0.35rem">
                <Text fontWeight={600} fontSize="0.9rem">
                  Wi-Fi
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Wi-Fi network name{" "}
                    :
                  </Text>{" "}
                  {device.lastSsid || "—"}
                </Text>
                <DataFieldWithHelp
                  label="RSSI (dBm)"
                  value={typeof device.lastRssi === "number" ? `${device.lastRssi} dBm` : "—"}
                  helpText="Wi-Fi signal strength. Higher (closer to 0 dBm) means stronger Wi-Fi. Values below −80 dBm may cause unreliable connectivity."
                />
                <DataFieldWithHelp
                  label="Connection count"
                  value={device.connectionCount ?? 0}
                  helpText="Number of times this device successfully connected to Wi-Fi. A rapidly increasing count may indicate unstable Wi-Fi."
                />
                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                <Text fontWeight={600} fontSize="0.9rem">
                  Boot / uptime
                </Text>
                <DataFieldWithHelp
                  label="Last boot at"
                  value={device.lastBootAt ? formatDateTime(device.lastBootAt) : "—"}
                  helpText="Time when the device last restarted. Unexpected restarts may indicate issues."
                />
                <DataFieldWithHelp
                  label="Reboot count"
                  value={device.rebootCount ?? 0}
                  helpText="Number of times the device restarted."
                />
                <DataFieldWithHelp
                  label="Last uptime"
                  value={typeof device.lastUptime === "number" ? formatUptime(device.lastUptime) : "—"}
                  helpText="How long the device ran since its last restart."
                />

                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                <Text fontWeight={600} fontSize="0.9rem">
                  Versions
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Firmware version:
                  </Text>{" "}
                  {device.firmware || lastTelemSnapshot?.fw || "—"}
                </Text>
                <Text fontSize="0.85rem">
                  <Text as="span" fontWeight={600}>
                    Hardware version:
                  </Text>{" "}
                  {device.modelRev || lastTelemSnapshot?.modelRev || "—"}
                </Text>
              </Flex>
            ) : (
              loadingDevice && <Loader />
            )}
          </Card>
        </Flex>

        {/* Right column */}
        <Flex
          direction="column"
          gap="1rem"
          width={{ base: "100%", large: "62%" }}
        >
          {/* Telemetry history */}
          <Card variation="outlined">
            <Heading level={5} marginBottom="0.5rem">
              Telemetry (last 24 hours)
            </Heading>

            {telemetryError && (
              <Alert
                variation="error"
                marginBottom="0.5rem"
                isDismissible={true}
                onDismiss={() => setTelemetryError("")}
              >
                {telemetryError}
              </Alert>
            )}

            {loadingTelemetry && telemetry.length === 0 && <Loader />}

            {!loadingTelemetry && telemetry.length === 0 && !telemetryError && (
              <Text variation="tertiary" fontSize="0.85rem">
                No telemetry samples received in the last 24 hours.
              </Text>
            )}

            {telemetry.length > 0 && (
              <View overflowX="auto">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell as="th">Time</TableCell>
                      <TableCell as="th">Temp (°C)</TableCell>
                      <TableCell as="th">RH (%)</TableCell>
                      <TableCell as="th">Batt (V)</TableCell>
                      <TableCell as="th">
                        RSSI (dBm)
                        <View
                          as="span"
                          display={{ base: "none", large: "inline-block" }}  // hide on mobile, show on desktop
                          marginLeft="0.25rem"
                        >
                          <span
                            title="Wi-Fi signal strength. Higher (closer to 0 dBm) means stronger Wi-Fi. Values below −80 dBm may cause unreliable connectivity."
                            style={{ display: "inline-flex" }}
                          >
                            <Info
                              size={14}
                              style={{ cursor: "help", opacity: 0.65 }}
                            />
                          </span>
                        </View>
                      </TableCell>
                      <TableCell as="th">
                        Water Sensor 1
                        <View
                          as="span"
                          display={{ base: "none", large: "inline-block" }} 
                          marginLeft="0.25rem"
                        >
                          <span
                            title="One of the water sensors on the device. Your device has 2 water sensors."
                            style={{ display: "inline-flex" }}
                          >
                            <Info
                              size={14}
                              style={{ cursor: "help", opacity: 0.65 }}
                            />
                          </span>
                        </View>
                      </TableCell>
                      <TableCell as="th">
                        Water Sensor 2
                        <View
                          as="span"
                          display={{ base: "none", large: "inline-block" }} 
                          marginLeft="0.25rem"
                        >
                          <span
                            title="One of the water sensors on the device. Your device has 2 water sensors."
                            style={{ display: "inline-flex" }}
                          >
                            <Info
                              size={14}
                              style={{ cursor: "help", opacity: 0.65 }}
                            />
                          </span>
                        </View>
                      </TableCell>
                      <TableCell as="th">
                        Network Name
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {telemetry.map((t, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatShortDateTime(t.received_at)}</TableCell>
                        <TableCell>
                          {typeof t.tempC === "number"
                            ? t.tempC.toFixed(1)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {typeof t.rh === "number" ? t.rh.toFixed(1) : "—"}
                        </TableCell>
                        <TableCell>
                          {typeof t.battV === "number"
                            ? t.battV.toFixed(2)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {typeof t.rssi === "number" ? t.rssi : "—"}
                        </TableCell>
                        <TableCell>{t.leak1 ? "Wet" : "Dry"}</TableCell>
                        <TableCell>{t.leak2 ? "Wet" : "Dry"}</TableCell>
                        <TableCell>{t.ssid || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </View>
            )}
          </Card>

          {/* Recent events */}
          <Card variation="outlined">
            <Heading level={5} marginBottom="0.5rem">
              Recent events
            </Heading>

            {eventsError && (
              <Alert
                variation="error"
                marginBottom="0.5rem"
                isDismissible={true}
                onDismiss={() => setEventsError("")}
              >
                {eventsError}
              </Alert>
            )}

            {loadingEvents && events.length === 0 && <Loader />}

            {!loadingEvents && events.length === 0 && !eventsError && (
              <Text variation="tertiary" fontSize="0.85rem">
                No events yet for this device.
              </Text>
            )}

            {events.length > 0 && (
              <View overflowX="auto">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell as="th">Time</TableCell>
                      <TableCell as="th">Type</TableCell>
                      <TableCell as="th">Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((ev) => (
                      <TableRow key={ev.id}>
                        <TableCell>{formatShortDateTime(ev.ts)}</TableCell>
                        <TableCell>{ev.type}</TableCell>
                        <TableCell>{ev.message || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </View>
            )}

            {/* TODO: need to code this into events page. */}
            <Flex justifyContent="flex-end" marginTop="0.75rem">
              <Button
                size="small"
                variation="link"
                onClick={() => {
                  // navigate to your alerts/events page with device filter
                  navigate(`/events?deviceId=${encodeURIComponent(deviceId)}`);
                }}
              >
                View all events for this device →
              </Button>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </View>
  );
}
